import { useQuery } from '@tanstack/react-query';
import { queryExecutionRuns } from '@/features/executionRuns/data/executionRuns';
import { queryResultsFiles, queryUploads } from '@/features/uploads/data';
import { queryKeys } from '@/shared/hooks/queryKeys';
import type { ApiFilter } from '@/ui/composites/grid/gridUtils';

const TAG_OPTIONS_PAGE_SIZE = 100;

const removeTagFilters = (filters: ApiFilter[]): ApiFilter[] =>
  filters.filter((filter) => filter.field !== 'tags');

const extractSortedTags = (rows: Array<{ tags?: string[] }>): string[] => {
  return Array.from(
    new Set(
      rows.flatMap((row) =>
        Array.isArray(row.tags)
          ? row.tags.filter((tag): tag is string => typeof tag === 'string' && tag.length > 0)
          : [],
      ),
    ),
  ).sort((left, right) => left.localeCompare(right));
};

const collectAllExecutionRunTags = async (
  orgId: string | null,
  filters: ApiFilter[],
): Promise<string[]> => {
  const firstPage = await queryExecutionRuns(
    {
      page: 1,
      pageSize: TAG_OPTIONS_PAGE_SIZE,
      filters,
      includeSummary: false,
    },
    orgId ?? undefined,
  );
  const rows = [...firstPage.items];
  const totalPages = Math.max(1, Math.ceil(firstPage.total / TAG_OPTIONS_PAGE_SIZE));
  for (let page = 2; page <= totalPages; page += 1) {
    const nextPage = await queryExecutionRuns(
      {
        page,
        pageSize: TAG_OPTIONS_PAGE_SIZE,
        filters,
        includeSummary: false,
      },
      orgId ?? undefined,
    );
    rows.push(...nextPage.items);
  }
  return extractSortedTags(rows);
};

const collectAllResultsFileTags = async (
  orgId: string | null,
  filters: ApiFilter[],
): Promise<string[]> => {
  const firstPage = await queryResultsFiles(
    {
      page: 1,
      pageSize: TAG_OPTIONS_PAGE_SIZE,
      filters,
      includeSummary: false,
      include: { project: false, createdBy: false, createdByEmail: false },
    },
    orgId ?? undefined,
  );
  const rows = [...firstPage.items];
  const totalPages = Math.max(1, Math.ceil(firstPage.total / TAG_OPTIONS_PAGE_SIZE));
  for (let page = 2; page <= totalPages; page += 1) {
    const nextPage = await queryResultsFiles(
      {
        page,
        pageSize: TAG_OPTIONS_PAGE_SIZE,
        filters,
        includeSummary: false,
        include: { project: false, createdBy: false, createdByEmail: false },
      },
      orgId ?? undefined,
    );
    rows.push(...nextPage.items);
  }
  return extractSortedTags(rows);
};

const collectAllUploadTags = async (
  orgId: string | null,
  filters: ApiFilter[],
): Promise<string[]> => {
  const firstPage = await queryUploads(
    {
      page: 1,
      pageSize: TAG_OPTIONS_PAGE_SIZE,
      filters,
      includeSummary: false,
    },
    orgId ?? undefined,
  );
  const rows = [...firstPage.items];
  const totalPages = Math.max(1, Math.ceil(firstPage.total / TAG_OPTIONS_PAGE_SIZE));
  for (let page = 2; page <= totalPages; page += 1) {
    const nextPage = await queryUploads(
      {
        page,
        pageSize: TAG_OPTIONS_PAGE_SIZE,
        filters,
        includeSummary: false,
      },
      orgId ?? undefined,
    );
    rows.push(...nextPage.items);
  }
  return extractSortedTags(rows);
};

export const useExecutionRunsTagOptions = (params: {
  orgId: string | null;
  projectId: string | null;
  filters: ApiFilter[];
  enabled: boolean;
}): { availableTags: string[]; isLoading: boolean } => {
  const normalizedFilters = removeTagFilters(params.filters);
  const filtersKey = JSON.stringify(normalizedFilters);
  const query = useQuery({
    queryKey: queryKeys.uploadTableTags(
      'execution-runs',
      params.orgId,
      params.projectId,
      filtersKey,
    ),
    enabled: params.enabled,
    queryFn: async () => await collectAllExecutionRunTags(params.orgId, normalizedFilters),
    staleTime: 30_000,
  });
  return { availableTags: query.data ?? [], isLoading: query.isLoading };
};

export const useResultsFilesTagOptions = (params: {
  orgId: string | null;
  projectId: string | null;
  filters: ApiFilter[];
  enabled: boolean;
}): { availableTags: string[]; isLoading: boolean } => {
  const normalizedFilters = removeTagFilters(params.filters);
  const filtersKey = JSON.stringify(normalizedFilters);
  const query = useQuery({
    queryKey: queryKeys.uploadTableTags(
      'results-files',
      params.orgId,
      params.projectId,
      filtersKey,
    ),
    enabled: params.enabled,
    queryFn: async () => await collectAllResultsFileTags(params.orgId, normalizedFilters),
    staleTime: 30_000,
  });
  return { availableTags: query.data ?? [], isLoading: query.isLoading };
};

export const useUploadsTagOptions = (params: {
  orgId: string | null;
  projectId: string | null;
  filters: ApiFilter[];
  enabled: boolean;
}): { availableTags: string[]; isLoading: boolean } => {
  const normalizedFilters = removeTagFilters(params.filters);
  const filtersKey = JSON.stringify(normalizedFilters);
  const query = useQuery({
    queryKey: queryKeys.uploadTableTags('uploads', params.orgId, params.projectId, filtersKey),
    enabled: params.enabled,
    queryFn: async () => await collectAllUploadTags(params.orgId, normalizedFilters),
    staleTime: 30_000,
  });
  return { availableTags: query.data ?? [], isLoading: query.isLoading };
};

export const executionRunsPageTagOptionsTestUtils = {
  removeTagFilters,
  extractSortedTags,
};
